"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { GuidedProductShot } from "./guided";
import { guidedMomentFor, type GuidedMoment } from "./moments";
import type { ShotStory } from "./story";

/**
 * Holds a guided shot's first frame, rendered on the server, until the
 * interactive shot takes over: once the frame nears the viewport or a
 * visitor reaches for it. A click on a moment before then is kept and
 * opened when it does. Until this island hydrates the frame is inert, so an
 * early click cannot follow a product link off the page.
 *
 * The interactive shot ships with the route rather than as a lazy chunk: a
 * dynamic import here makes the markdown and motion libraries it shares
 * with the home reachable from an async chunk, and Turbopack then stops
 * merging them for every page, the home included (+15 KB gzip measured).
 */
export function GuidedIsland({
  tone,
  story,
  label,
  children,
}: {
  tone: AppThemeTone;
  /** The session the shot tells; the kettle comparison by default. */
  story?: ShotStory;
  label?: string;
  children: ReactNode;
}) {
  const islandRef = useRef<HTMLDivElement>(null);
  const pending = useRef<GuidedMoment | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [active, setActive] = useState(false);

  const activate = useCallback(() => setActive(true), []);

  useEffect(() => {
    setHydrated(true);
    const island = islandRef.current;
    if (!island || typeof IntersectionObserver === "undefined") {
      activate();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        activate();
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(island);
    return () => observer.disconnect();
  }, [activate]);

  return (
    <div
      ref={islandRef}
      inert={hydrated ? undefined : true}
      onPointerEnter={activate}
      onFocusCapture={activate}
      onClickCapture={(event) => {
        if (active) return;
        const moment = guidedMomentFor(event.target);
        if (moment) pending.current = moment;
        if (moment || (event.target instanceof Element && event.target.closest("a"))) {
          event.preventDefault();
        }
        activate();
      }}
    >
      {active ? (
        <GuidedProductShot tone={tone} initialMoment={pending.current} story={story} label={label} />
      ) : (
        children
      )}
    </div>
  );
}
